import { Component, ViewChild, Input, OnInit } from '@angular/core';
import { NgApexchartsModule } from 'ng-apexcharts';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { PowerTrainService } from '../services/powertrain.service';
import { BaseUrlConstantsService } from '../services/base-url-constants.service';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';

@Component({
  selector: 'app-stacked-line-chart',
  standalone: true,
  imports: [NgApexchartsModule, CommonModule, FormsModule, MatButtonModule, MatIconModule, MatMenuModule],
  templateUrl: './stacked-line-chart.component.html',
  styleUrls: ['./stacked-line-chart.component.scss'],
})
export class StackedLineChartComponent implements OnInit {
  @ViewChild('chart') chart!: any;
  @Input("assetInventoryIds") assetInventoryIds: any[] = [];
  @Input("graphType") graphType: number = 0;

  // Chart data
  series: any[] = [];
  allSeries: any[] = [];
  chartOptions: any = {
    chart: {
      type: 'area',
      height: 400,
      toolbar: { show: true }, // Enable toolbar for zoom controls
      animations: {
        enabled: true,
        easing: 'easeinout',
        speed: 800
      },
      background: '#ffffff'
    },
    xaxis: { categories: [] },
    yaxis: { max: 100 },
    annotations: { yaxis: [] },
    stroke: { curve: 'smooth', width: 2 },
    markers: { size: 0, hover: { size: 6 } },
    dropShadow: {
      enabled: true,
      top: 2,
      left: 2,
      blur: 4,
      opacity: 0.3,
    },
    tooltip: {
      enabled: true,
      shared: true,
      intersect: false,
      x: {
        format: 'yyyy-MM-dd HH:mm:ss', // Ensure datetime format includes time
        formatter: function(val: number) {
          // Format the timestamp to include date and time
          const date = new Date(val);
          return date.toLocaleString(); // This will display both date and time in local format
        }
      }
    },
    legend: {
      position: 'top',
      horizontalAlign: 'left', // Positioning the legend to the right
      floating: false,
      fontSize: '12px',
      offsetY: -30 // Adjust this value as needed to position the legend properly
    },
    grid: {
      borderColor: '#e0e0e0',
      strokeDashArray: 4,
      yaxis: {
        lines: {
          show: true
        }
      }
    },
    theme: {
      mode: 'light',
      palette: 'palette8'
    },
    dataLabels: {
      enabled: false
    }
  };
  allSeriesWithMetadata: {
    series: any;
    type: string;
    deviceName: string;
  }[] = [];

  // Filter options
  selectedEquipmentLocation: string[] = [];
  availableEquipmentLocation: string[] = [];
  showXAxis: boolean = true;
  showYAxis: boolean = true;
  showZAxis: boolean = true;
  isDropdownOpen: boolean = false;
  isAxisDropdownOpen: boolean = false;
  isDeviceDropdownOpen: boolean = false;
  isDarkMode: boolean = false;

  // Threshold values
  green: number = 0;
  yellow: number = 0;
  red: number = 0;
  tempLabelX: string = '';
  tempLabelY: string = '';
  tempLabelZ: string = '';

  // Loading state
  isLoading: boolean = false;
  token: string | null = null;

  constructor(private powerTrainService: PowerTrainService) {}

  ngOnInit(): void {
    if (this.assetInventoryIds && this.assetInventoryIds.length) {
      this.availableEquipmentLocation = this.assetInventoryIds.map(item =>
        item.sensorDNames || `Device ${this.availableEquipmentLocation.length + 1}`
      );
      this.selectedEquipmentLocation = [];
      this.assetInventoryIds.forEach(item => {
        if (item.assetType === 'MOTOR') {
          this.selectedEquipmentLocation.push(item.sensorDNames || `Device ${this.selectedEquipmentLocation.length + 1}`);
        }
      });
      this.fetchAccelerationSensorData();
    }
    this.token = localStorage.getItem('token');

    // Add event listener for closing dropdown when clicking outside
    document.addEventListener('click', this.closeDropdownOnOutsideClick.bind(this));
    if(this.graphType === 2 || this.graphType === 3) {
      this.showYAxis = false;
      this.showZAxis = false;
    }
  }

  ngOnDestroy(): void {
    // Remove event listener when component is destroyed
    document.removeEventListener('click', this.closeDropdownOnOutsideClick.bind(this));
  }

  private getSensorIds(graphType: number): string {
    switch (graphType) {
      case 0:
        return '6,7,8';
      case 1:
        return '29,30,31';
      case 3:
        return '36';
      case 4:
        return '1,2,3';
      default:
        return '29,30,31';
    }
  }

  async fetchAccelerationSensorData() {
    if(!this.assetInventoryIds.length) return;
    this.isLoading = true;
    try {
      const sensorIds = this.getSensorIds(this.graphType);
      const subSystemId = this.assetInventoryIds[0].subSystemId;
      const clientId = this.assetInventoryIds[0].clientId;
      const response = await this.powerTrainService.fetchPowertrainTrendsChart(subSystemId, sensorIds, this.token, this.graphType);

      this.allSeriesWithMetadata = [];
      const { green, yellow, red, chartXdata, chartYdata, chartZdata, labelx, labely, labelz } = response.data;
      this.tempLabelX = labelx;
      this.tempLabelY = labely;
      this.tempLabelZ = labelz;

      const deviceIdToSensor = new Map<number, string>();
      this.assetInventoryIds.forEach(asset =>
        deviceIdToSensor.set(asset.inventory_id, asset.sensorDNames)
      );

      // Process data for each device
      this.allSeriesWithMetadata = [];
      this.assetInventoryIds.forEach(asset => {
        const deviceId = asset.inventory_id;
        const sensorName = deviceIdToSensor.get(deviceId) || `Device ${deviceId}`;

        // Process X-axis data
        const xData = chartXdata
          .filter(d => d.deviceId === deviceId)
          // .sort((a, b) => new Date(a.x).getTime() - new Date(b.x).getTime())
          .map(d => ({ x: new Date(d.x).getTime(), y: Math.min(d.y, red) }));

        if (xData.length) {
          this.allSeriesWithMetadata.push({
            series: { name: `${sensorName} - ${this.tempLabelX}`, data: xData },
            type: 'X',
            deviceName: sensorName
          });
        }

        // Process Y-axis data
        if(this.graphType !== 4 && chartYdata && chartYdata.length && chartZdata && chartZdata.length) {
          const yData = chartYdata
            .filter(d => d.deviceId === deviceId)
            // .sort((a, b) => new Date(a.x).getTime() - new Date(b.x).getTime())
            .map(d => ({ x: new Date(d.x).getTime(), y: Math.min(d.y, red) }));

          if (yData.length) {
            this.allSeriesWithMetadata.push({
              series: { name: `${sensorName} - ${this.tempLabelY}`, data: yData },
              type: 'Y',
              deviceName: sensorName
            });
          }

          // Process Z-axis data
          const zData = chartZdata
            .filter(d => d.deviceId === deviceId)
            // .sort((a, b) => new Date(a.x).getTime() - new Date(b.x).getTime())
            .map(d => ({ x: new Date(d.x).getTime(), y: Math.min(d.y, red) }));

          if (zData.length) {
            this.allSeriesWithMetadata.push({
              series: { name: `${sensorName} Z - ${this.tempLabelZ}`, data: zData },
              type: 'Z',
              deviceName: sensorName
            });
          }
        }
      });

      // Update thresholds and filters
      this.green = green;
      this.yellow = yellow;
      this.red = red;
      this.applyFilters();

      // Configure chart options
      this.updateChartOptions();
    }
    catch (error) {
      console.error('Error fetching data:', error);
    }
    finally {
      this.isLoading = false;
    }
  }

  // Apply filters based on selected MAC addresses and axes
  applyFilters() {
    const filtered = this.allSeriesWithMetadata.filter(item => {
      const isDeviceSelected = this.selectedEquipmentLocation.includes(item.deviceName);
      const isAxisVisible =
        (item.type === 'X' && this.showXAxis) ||
        (item.type === 'Y' && this.showYAxis) ||
        (item.type === 'Z' && this.showZAxis);
      return isDeviceSelected && isAxisVisible;
    });
    this.series = filtered.map(item => item.series);
  }

  toggleMacAddress(equipmentLocation: string) {
    const index = this.selectedEquipmentLocation.indexOf(equipmentLocation);
    if (index === -1) {
      this.selectedEquipmentLocation.push(equipmentLocation);
    } else {
      this.selectedEquipmentLocation.splice(index, 1);
    }
    this.applyFilters();
  }

  // Select all MAC addresses
  selectAllEquipmentLocation() {
    this.selectedEquipmentLocation = [...this.availableEquipmentLocation];
    this.applyFilters();
  }

  // Clear all MAC address selections
  clearAllEquipmentLocation() {
    this.selectedEquipmentLocation = [];
    this.applyFilters();
  }

  // Toggle axis visibility
  toggleAxis(axis: string) {
    if (axis === 'X') this.showXAxis = !this.showXAxis;
    if (axis === 'Y') this.showYAxis = !this.showYAxis;
    if (axis === 'Z') this.showZAxis = !this.showZAxis;
    this.applyFilters();
  }

  toggleDropdown(type: string) {
    if (type === 'axis') {
      this.isAxisDropdownOpen = !this.isAxisDropdownOpen;
      this.isDeviceDropdownOpen = false;
    } else if (type === 'device') {
      this.isDeviceDropdownOpen = !this.isDeviceDropdownOpen;
      this.isAxisDropdownOpen = false;
    }
  }

  // Close dropdowns when clicking outside
  closeDropdownOnOutsideClick(event: MouseEvent) {
    const target = event.target as HTMLElement;

    // Check for axis dropdown
    if (this.isAxisDropdownOpen) {
      const axisDropdownElement = target.closest('.axis-dropdown');
      if (!axisDropdownElement) {
        this.isAxisDropdownOpen = false;
      }
    }

    // Check for device dropdown
    if (this.isDeviceDropdownOpen) {
      const deviceDropdownElement = target.closest('.device-dropdown');
      if (!deviceDropdownElement) {
        this.isDeviceDropdownOpen = false;
      }
    }
  }

  // Toggle dark mode
  toggleDarkMode() {
    this.isDarkMode = !this.isDarkMode;
    this.updateChartOptions();
  }

  // Update chart options
  updateChartOptions() {
    // Light/dark mode specific colors
    const bgColor = this.isDarkMode ? '#000000' : '#ffffff';
    const textColor = this.isDarkMode ? '#ffffff' : '#333333';
    const gridColor = this.isDarkMode ? '#444444' : '#e0e0e0';

    // Threshold colors with different opacity based on mode
    const greenColor = 'rgba(0, 255, 0, 0.6)';
    const yellowColor = 'rgba(255, 255, 0, 0.6)';
    const redColor = 'rgba(255, 0, 0, 0.6)';

    this.chartOptions = {
      ...this.chartOptions,
      chart: {
        ...this.chartOptions.chart,
        background: bgColor,
      },
      xaxis: {
        type: 'datetime',
        labels: {
          format: 'yyyy-MM-dd HH:mm:ss',
          datetimeUTC: false,
          rotate: -45,
          style: {
            fontSize: '11px',
            colors: textColor
          }
        }
      },
      yaxis: {
        max: this.red,
        labels: {
          formatter: function (val) {
            return Math.round(val);
          },
          style: {
            colors: textColor
          }
        }
      },
      annotations: {
        yaxis: [
          {
            y: 0,
            y2: this.green,
            fillColor: greenColor,
            label: {
              text: 'Healthy',
              style: {
                color: textColor,
                background: this.isDarkMode ? '#272727' : '#f5f5f5'
              }
            }
          },
          {
            y: this.green,
            y2: this.yellow,
            fillColor: yellowColor,
            label: {
              text: 'Unsatisfactory',
              style: {
                color: textColor,
                background: this.isDarkMode ? '#272727' : '#f5f5f5'
              }
            }
          },
          {
            y: this.yellow,
            y2: this.red,
            fillColor: redColor,
            label: {
              text: 'Unacceptable',
              style: {
                color: textColor,
                background: this.isDarkMode ? '#272727' : '#f5f5f5'
              }
            }
          }
        ]
      },
      grid: {
        borderColor: gridColor,
        strokeDashArray: 4,
        yaxis: {
          lines: {
            show: true
          }
        }
      },
      theme: {
        mode: this.isDarkMode ? 'dark' : 'light',
        palette: 'palette8'
      },
      legend: {
        ...this.chartOptions.legend,
        labels: {
          colors: textColor
        }
      },
      tooltip: {
        ...this.chartOptions.tooltip,
        theme: this.isDarkMode ? 'dark' : 'light'
      }
    };
  }
}